import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, BookOpen, Scale, Users2 } from "lucide-react";
import { AmbientLight } from "@/components/brand/AmbientLight";
import ChapterMark from "@/components/brand/ChapterMark";
import { ConcentricMonogram } from "@/components/brand/ConcentricMonogram";
import { BRAND } from "@/data/brand";

const ITEMS = [
  {
    n: "01",
    icon: ShieldCheck,
    title: "Florida Licensing",
    body:
      "AG Public Adjusting operates under a Florida Public Adjuster Firm license. All assigned adjusters are individually licensed and active in good standing.",
  },
  {
    n: "02",
    icon: Scale,
    title: "Regulatory Compliance",
    body:
      "Engagement contracts, fee structures, and timelines follow Florida Statute § 626.854. Documentation is built to withstand regulatory review.",
  },
  {
    n: "03",
    icon: BookOpen,
    title: "Professional Standards",
    body:
      "Continuing education, peer review of complex files, and a written methodology that governs every claim from intake to recovery.",
  },
  {
    n: "04",
    icon: Users2,
    title: "Policyholder Representation",
    body:
      "The firm represents the policyholder. The firm does not represent insurance carriers. This boundary is unconditional.",
  },
];

export default function Licensing() {
  return (
    <section
      data-testid="licensing-section"
      className="relative py-24 sm:py-32 lg:py-52 overflow-hidden bg-midnight-deeper"
    >
      <AmbientLight size={1000} color="gold" intensity={0.22} style={{ top: "30%", left: "50%", transform: "translate(-50%, -50%)" }} />
      <AmbientLight size={700} color="blue" intensity={0.25} style={{ bottom: "5%", right: "-5%" }} />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <ChapterMark index="12" label="Licensing & Compliance" align="center" className="mb-14" />

        <div className="text-center max-w-3xl mx-auto mb-20 lg:mb-24">
          <h2 className="font-display-tight text-white text-[clamp(2.5rem,5.2vw,4.75rem)] leading-[0.96] tracking-[-0.045em]">
            Trust is structural.
            <br />
            <span className="gold-metallic font-display font-light">Not a tagline.</span>
          </h2>
          <div className="mx-auto mt-12 h-px w-20 bg-gradient-to-r from-transparent via-gold to-transparent" />
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Seal column */}
          <div className="lg:col-span-5 flex flex-col items-center lg:items-start">
            <div className="relative">
              <ConcentricMonogram size={360} variant="gold" glowColor="gold" rotateRings />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center -mt-2">
                  <div className="font-mono text-[0.55rem] text-gold tracking-[0.4em] uppercase mb-1">
                    Florida
                  </div>
                  <div className="font-mono text-[0.55rem] text-gold/70 tracking-[0.4em] uppercase">
                    Public Adjuster Firm
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-12 text-center lg:text-left">
              <div className="font-mono text-[0.62rem] text-gold tracking-[0.32em] uppercase mb-3">
                License Number
              </div>
              <div className="font-display text-2xl lg:text-3xl text-white font-light tracking-[-0.025em]">
                {BRAND.licenseNumber}
              </div>
              <p className="mt-6 text-ag-text-secondary text-sm lg:text-[0.95rem] leading-[1.7] font-light max-w-sm">
                Each engagement is executed under written contract aligned with Florida statute and
                Department of Financial Services standards.
              </p>
            </div>
          </div>

          {/* Items column */}
          <div className="lg:col-span-7">
            <div className="divide-y divide-white/[0.06]">
              {ITEMS.map((item, i) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.7, delay: i * 0.1 }}
                    className="group py-10 lg:py-12 first:pt-0"
                    data-testid={`trust-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div className="grid grid-cols-12 gap-5 lg:gap-8 items-start">
                      <div className="col-span-2 lg:col-span-2 flex flex-col items-start">
                        <span className="font-mono text-[0.6rem] text-gold tracking-[0.32em]">{item.n}</span>
                        <div className="mt-3 h-px w-8 bg-gold/40 group-hover:w-14 group-hover:bg-gold transition-all duration-700" />
                        <Icon className="mt-5 w-5 h-5 text-gold/80" strokeWidth={1.2} />
                      </div>
                      <div className="col-span-10 lg:col-span-10">
                        <h3 className="font-display text-xl lg:text-2xl text-white mb-4 font-light tracking-[-0.025em]">
                          {item.title}
                        </h3>
                        <p className="text-ag-text-secondary text-[0.95rem] lg:text-[1rem] leading-[1.75] font-light max-w-xl">
                          {item.body}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
